import clsx from 'clsx';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AcceptMarketingMessagesModal from 'components/curo-components/common/AcceptMarketingMessagesModal';
import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import EmailInput from 'components/curo-components/common/EmailInput';
import PhoneNumberInput from 'components/curo-components/common/PhoneNumberInput';
import SSNInput from 'components/curo-components/common/SSNInput';
import TextInput from 'components/curo-components/common/TextInput';
import commonStyles from 'components/curo-components/forms/Forms.module.scss';
import { FormProps } from 'components/curo-components/forms/types';
import Checkbox from 'components/digifi-wrappers/Checkbox';
import { GoogleRecaptchaActionsType } from 'enums/GoogleRecaptchaActionsType';
import { CuroAppRoute } from 'enums/curo/CuroAppRoute';
import { EnvironmentVariable } from 'enums/curo/EnvironmentVariable';
import { envs } from 'envs';
import { useGetAndVerifyReCaptchaToken } from 'hooks/useGetAndVerifyReCaptchaToken';
import { createFormikHelpers } from 'utils/curo-helpers/formHelpers';
import { NOT_DIGITS_REGEX, formatString } from 'utils/curo-helpers/validation';

import styles from './WelcomeForm.module.scss';
import { WelcomeFormFields, IWelcomeFormValues, welcomeFormLabels } from './constants';
import { applyFormValidationSchema } from './validationSchema';

const LAST_6_DIGITS_SSN_FORMAT = '##-####';

type WelcomeFormProps = FormProps<WelcomeFormFields, IWelcomeFormValues>;

const WelcomeForm = ({ handleSubmit, initialData }: WelcomeFormProps) => {
  const [isAcceptMarketingMessagesModalOpen, setIsAcceptMarketingMessagesModalOpen] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const executeRecaptcha = useGetAndVerifyReCaptchaToken();

  return (
    <Formik
      initialValues={initialData}
      validationSchema={applyFormValidationSchema}
      validateOnBlur
      validateOnChange
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ touched, errors, values, submitForm, isValid, setFieldValue, setTouched }) => {
        const handleContinue = async () => {
          if (envs[EnvironmentVariable.Environment] !== 'prod') {
            await submitForm();
            return;
          }

          const isValidToGoNext = await executeRecaptcha(GoogleRecaptchaActionsType.DmWelcome);

          if (isValidToGoNext) {
            await submitForm();
          }
        };

        const {
          handleInputChange,
          handleTouched,
          getInputErrorMessage,
          handleResetExternalError,
          hasExternalError,
        } = createFormikHelpers<WelcomeFormFields, IWelcomeFormValues>(
          setFieldValue,
          setTouched,
          touched,
          errors,
        );

        return (
          <section className={clsx(styles.formWrapper, commonStyles.formWrapper)}>
            <div className={styles.inputWrapper}>
              <TextInput
                labelTitle={welcomeFormLabels[WelcomeFormFields.BorrowerZipOrPostalCode]}
                value={values[WelcomeFormFields.BorrowerZipOrPostalCode]}
                onChange={handleInputChange(WelcomeFormFields.BorrowerZipOrPostalCode)}
                formatter={formatString(NOT_DIGITS_REGEX)}
                maxLength={5}
                onBlur={handleTouched(WelcomeFormFields.BorrowerZipOrPostalCode)}
                errorMessage={getInputErrorMessage(WelcomeFormFields.BorrowerZipOrPostalCode)}
                required
              />
              <SSNInput
                labelTitle={welcomeFormLabels[WelcomeFormFields.BorrowerLast6DigitsSSN]}
                value={values[WelcomeFormFields.BorrowerLast6DigitsSSN]}
                onChange={handleInputChange(WelcomeFormFields.BorrowerLast6DigitsSSN)}
                onBlur={handleTouched(WelcomeFormFields.BorrowerLast6DigitsSSN)}
                errorMessage={getInputErrorMessage(WelcomeFormFields.BorrowerLast6DigitsSSN)}
                ssnFormat={LAST_6_DIGITS_SSN_FORMAT}
                placeholder={LAST_6_DIGITS_SSN_FORMAT}
                required
              />
              <EmailInput
                labelTitle={welcomeFormLabels[WelcomeFormFields.BorrowerEmail]}
                value={values[WelcomeFormFields.BorrowerEmail]}
                onChange={handleInputChange(WelcomeFormFields.BorrowerEmail)}
                onBlur={handleTouched(WelcomeFormFields.BorrowerEmail)}
                errorMessage={getInputErrorMessage(WelcomeFormFields.BorrowerEmail)}
                onFocus={handleResetExternalError(WelcomeFormFields.BorrowerEmail)}
                required
              />
              <PhoneNumberInput
                labelTitle={welcomeFormLabels[WelcomeFormFields.BorrowerPhone]}
                value={values[WelcomeFormFields.BorrowerPhone]}
                onChange={handleInputChange(WelcomeFormFields.BorrowerPhone)}
                onBlur={handleTouched(WelcomeFormFields.BorrowerPhone)}
                errorMessage={getInputErrorMessage(WelcomeFormFields.BorrowerPhone)}
                onFocus={handleResetExternalError(WelcomeFormFields.BorrowerPhone)}
                required
              />
            </div>
            <div className={styles.checkbox}>
              <Checkbox
                onClick={() => {
                  setFieldValue(
                    WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy,
                    !values[WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy],
                  );
                }}
                checked={values[WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy]}
                name={WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy}
              />
              <span>
                I agree to{' '}
                <span
                  className={styles.underline}
                  onClick={() => {
                    if (values[WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy]) {
                      setFieldValue(
                        WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy,
                        false,
                      );
                      return;
                    }
                    setIsAcceptMarketingMessagesModalOpen(true);
                  }}
                >
                  Telephone and Electronic Communication Policy
                </span>
              </span>
            </div>
            {isAcceptMarketingMessagesModalOpen && (
              <AcceptMarketingMessagesModal
                handleAccept={(isAccepted) => {
                  setFieldValue(
                    WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy,
                    isAccepted,
                  );
                  setIsAcceptMarketingMessagesModalOpen(false);
                }}
                onClose={() => setIsAcceptMarketingMessagesModalOpen(false)}
              />
            )}
            <ButtonWithLoadingState
              kind="primary"
              size="form"
              disabled={!isValid || hasExternalError}
              onClick={handleContinue}
            >
              Continue
            </ButtonWithLoadingState>

            <div className={styles.noteWrapper}>
              <div className={styles.noteTitle}>Didn’t get a mail?</div>
              <div className={styles.noteText}>
                No problem! Let’s{' '}
                <span
                  className={styles.underline}
                  onClick={() => navigate(CuroAppRoute.ApplyProcess)}
                >
                  {' '}
                  see what you are pre-qualified for
                </span>{' '}
                within minutes with no credit impact.
              </div>
            </div>
          </section>
        );
      }}
    </Formik>
  );
};

export default WelcomeForm;
